/**
 * Spec 10.2.1 §2.1 / work item 4 — the Needs You home (approvals inbox).
 *
 * №53 (53.1) — CARD-FIRST. The page is a TWO-PANE: the decision rail on the left,
 * the open card on the right. Authorisations are a section WITHIN it, below the
 * decisions, not the surface's header.
 *
 * The header this comment used to carry ("authorisations first") described the
 * 10.2.1 composition and was already false before 53.1 touched anything — §22.6
 * put the decisions feed above approvals and left the paragraph behind. It is
 * recorded here because a 53.0 checkpoint read it as the current state and scoped
 * a re-ordering that was not needed; a stale doc comment costs exactly as much as
 * a stale test.
 *
 * №14.3.4 (F-5) — the load has FOUR distinct designed states, never conflated:
 * loading · empty (role-scoped: a member/non-signatory sees "nothing needs you",
 * because the server now returns [] for them, not an error) · denied (a genuine
 * permission refusal — teaching copy, no outage) · unreachable (a real transport
 * failure — the only place the "couldn't reach the ledger service" copy renders).
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { AuthRow } from "../../components/ledger/AuthRow";
import { HashChip } from "../../components/ledger/HashChip";
import { usePendingAuthorisations, useDecidedAuthorisations } from "../../hooks/use-ledger-approvals";
import { useStarterTasks } from "../../hooks/use-starter-tasks";
import { useWorkspaceActivity } from "../../hooks/use-workspace-activity";
import { useWorkspaceNeedsYou, useWorkflowRecordOutcome, type NeedsYouItem } from "../../hooks/use-workflow";
import { PencilGlyph, ClockGlyph, ForkGlyph } from "../../components/workflow/wf-glyphs";
import { mcpClient } from "../../../lib/mcp-client";
import { DecisionCard } from "../../components/collab/DecisionCard";
import { useAnswerNeedsYou, useSkipNeedsYou, usePlayTaskRun, useTakeOverTask } from "../../hooks/use-task-thread";
import { useJustAnswered, receiptFor, type AnsweredReceipt } from "../../hooks/use-just-answered";
import { useCoPanel } from "../../components/collab/co-panel-context";
import type { Task, NeedsYouOption, AnsweredDecision } from "../../../lib/types";
import { todayStartISO } from "../../components/collab/answered-window";
import { NEEDS_YOU_KEY } from "../../hooks/needs-you-key";

/** RC2-1 — the anchor + where-it-sits, one line ("Patel — settlement · Exchange"). */
function anchorLabelOf(t: Task): string | undefined {
  const a = t.anchor ?? null;
  if (!a) return undefined;
  return a.name && a.name !== a.no ? `${a.name} · ${a.no}` : a.no;
}

function SkeletonRow() {
  return (
    <div className="auth-row" style={{ cursor: "default" }}>
      <span className="skeleton" style={{ width: 26, height: 26, borderRadius: 6 }} />
      <div className="auth-row__main" style={{ width: "100%" }}>
        <div className="skeleton sk-line" style={{ width: "40%" }} />
        <div className="skeleton sk-line" style={{ width: "80%" }} />
        <div className="skeleton sk-line" style={{ width: "60%" }} />
      </div>
      <div className="auth-row__side"><div className="skeleton sk-line" style={{ width: 48 }} /></div>
    </div>
  );
}

/** The deep-link a workflow row reaches: the STEP on its stage (RC1-2), never the
 *  bare matter home — `#seg-N` lands where the step card / Mark-received lives. */
function stepHref(it: NeedsYouItem): string {
  return it.subjectNo
    ? `/app/place/${it.subjectType || "matter"}/${it.subjectNo}/process?lens=drive#seg-${it.segmentNo}`
    : "#";
}

/**
 * №22.6 (D-NY3) — THE ROW FACTS. This row used to be handed `assigneeKind`,
 * `assigneeCode`, `dueDate` and `waitingOn` and render none of them; the whole
 * defect was that it dropped four fields it was already being sent. It now
 * renders exactly what the server computed — the owner chip, the due date with
 * its lateness STATED, and the why-now line — and derives nothing itself.
 *
 * The three facts answer, in order, David's three questions: "who owns them",
 * "are they late", "why do they need me right now".
 */
function RowFacts({ it }: { it: NeedsYouItem }) {
  const parts: ReactNode[] = [];
  // The console and the worker deploy separately, and a console running ahead of
  // its worker receives the PRE-CONTRACT row: no owner, no due block, no why-now.
  // Render what arrived and nothing else — the alternative is a white screen on
  // `it.owner.kind`, which is a far worse failure than a row with fewer facts.
  const owner = it.owner;
  const due = it.due;
  // ① OWNER. "Unassigned" is a fact worth stating, not an absence to hide: an
  // unassigned ripe step in your remit is precisely why D-NY1 put it here.
  if (owner) {
    parts.push(
      <span key="owner" className="wf-assignee">
        {owner.kind === "unassigned" ? <span className="who">Unassigned</span> : <>{OWNER_PREFIX[owner.kind]} <span className="who">{owner.label}</span></>}
      </span>,
    );
  }
  // ② DUE, with its lateness. Overdue floats (D-NY2) — and says so, because a row
  // that sorts to the top without saying why teaches the reader nothing.
  if (due?.label) {
    parts.push(
      <span key="due" style={due.overdue ? { color: "var(--danger)", fontWeight: 650 } : undefined}>
        {due.label}
      </span>,
    );
  }
  // ③ WHY NOW. Built server-side from recorded fields (ready_at / the ripeness
  // antecedents / the wait's binding) — never an inference.
  if (it.whyNow) parts.push(<span key="why">{it.whyNow}</span>);
  return (
    <div className="d">
      {parts.map((p, i) => <span key={i}>{i > 0 ? " · " : ""}{p}</span>)}
    </div>
  );
}

const OWNER_PREFIX: Record<string, string> = { person: "", hire: "", role: "Role:" };

// №16 S4 — a workflow item as a Needs-You row (the shipped .listrow vocabulary).
// №22.6 (D-NY4) — rows that are GENUINELY just links stay rows. A row is a link
// when there is nothing to answer here: a ready step is work to go and do, a wait
// is a fact to mark received. A DECISION with declared outcomes is not a link —
// it is a call, and it renders as the card (see `WorkflowDecisionCard`).
function WorkflowGlanceRow({ it }: { it: NeedsYouItem }) {
  const isAgent = it.kind === "agent";
  const isWait = it.kind === "wait";
  const isDecision = it.kind === "decision";
  const markerCls = isAgent ? "wf-mark--ai" : isWait ? "wf-mark--wait" : isDecision ? "" : "listrow__marker--todo";
  return (
    <Link className={`listrow${isDecision ? " listrow--accent" : ""}`} to={stepHref(it)}>
      <span className={`listrow__marker ${markerCls}`} style={isDecision ? { background: "var(--warn)", color: "var(--accent-fg)" } : undefined} aria-hidden="true">
        {isAgent ? <PencilGlyph /> : isDecision ? <ForkGlyph /> : isWait ? <ClockGlyph /> : null}
      </span>
      <div className="listrow__main">
        <div className="t">{it.title}</div>
        <div className="d" style={{ opacity: 0.75 }}>{it.subtitle}</div>
        <RowFacts it={it} />
      </div>
      {/* RC1-4 — a wait surfaces its verb ("Mark received"), not a passive badge:
          waits are actionable everywhere they appear; the row reaches the step. */}
      <span className="listrow__go">{it.verb} →</span>
    </Link>
  );
}

/**
 * №22.6 (D-NY4) — A WORKFLOW DECISION IS THE CARD.
 *
 * "Weight is deliberate: consequence, not payload, sets prominence." A workflow
 * decision moves a matter's DECLARED process — often the most consequential thing
 * on the page — and it was rendering as one line and a link beside full escalation
 * cards, because the escalation happened to arrive carrying more JSON.
 *
 * This is NOT a second card. It is `DecisionCard`, the ONE component (D-CC4),
 * handed the decision's declared outcomes as its options. Each option records
 * through `workflow_record_outcome` — the engine's own single writer — so the
 * card executes the real verb exactly as an escalation's prepared call does.
 */
function WorkflowDecisionCard({ it }: { it: NeedsYouItem }) {
  const record = useWorkflowRecordOutcome();
  const [chosen, setChosen] = useState<number | null>(null);
  const [done, setDone] = useState<{ index: number; label: string; at: string } | null>(null);
  const options: NeedsYouOption[] = it.outcomes.map((o) => ({
    label: o.label || o.code,
    // The consequence is TRUE and structural: choosing an outcome opens that
    // branch and closes the others. It is not a per-option gloss we do not have —
    // saying more than the template declares would be invention.
    consequence: `Opens the “${o.label || o.code}” branch of ${it.title.replace(/^Decide:\s*/, "")}; the others go dead.`,
  }));
  return (
    <DecisionCard
      variant={done ? "full" : "compact"}
      reason={it.title.replace(/^Decide:\s*/, "")}
      options={options}
      // D-NY3's facts reach the card too — the card has always carried an anchor
      // line, and the why-now belongs beside it for exactly the same reason.
      //
      // The DUE label rides here as well, and it is not optional: D-NY2 says overdue
      // "floats with its lateness STATED". A decision that sorts to the top on an
      // inherited deadline and does not say so is the float without the statement —
      // it would teach the reader nothing about why it jumped the queue (O4).
      context={{ anchorLabel: it.subtitle, age: [it.due?.label, it.whyNow].filter(Boolean).join(" · ") }}
      resolved={done ? { optionIndex: done.index, label: done.label, at: done.at, outcome: "executed" } : null}
      onChoose={done ? undefined : (i) => {
        const outcome = it.outcomes[i];
        if (!outcome) return;
        setChosen(i);
        record.mutate(
          { instance_no: it.instanceNo, segment_no: it.segmentNo, outcome: outcome.code },
          { onSuccess: () => setDone({ index: i, label: outcome.label || outcome.code, at: new Date().toISOString() }) },
        );
      }}
      choosing={record.isPending}
      chosenIndex={record.isPending ? chosen : null}
      openThreadHref={stepHref(it)}
      error={record.isError ? ((record.error as Error)?.message ?? "That outcome couldn’t be recorded.") : null}
      data-testid="needs-you-decision"
    />
  );
}

const OUTCOME_PILL: Record<string, string> = {
  approved: "pill--approved", consumed: "pill--consumed", declined: "pill--declined",
  expired: "pill--expired", invalidated: "pill--invalid",
};

// №14.3.4 (F-5) — a role/permission refusal is NOT an outage. The approvals hook
// throws `Error(<server tool text>)`; a denied read carries the server's
// `area_forbidden` / `not_a_signatory` gloss, while a genuine transport failure is
// a fetch/network error with no such marker. The "couldn't reach the ledger
// service" copy may render ONLY for the latter (`unreachable`). (After the 14.3.4
// server change the inbox reads are self-scoped and return EMPTY for a member, so
// `denied` here is defence-in-depth for any other area-gated failure.)
function isDeniedError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /area_forbidden|forbidden|not_a_signatory|capability area|access[_ ]denied/i.test(msg);
}

export function NeedsYouPage() {
  const [searchParams] = useSearchParams();
  // Spec 10.2.1.2 — an approval magic link deep-links here with ?auth=<id>.
  const deepLinkAuth = searchParams.get("auth");
  const pending = usePendingAuthorisations();
  const decided = useDecidedAuthorisations();
  // №14 (14.6, D-SH6) — first-run starter rows; the server self-clears completed
  // ones, so we render only those still outstanding. Empty on non-shell firms.
  const starters = useStarterTasks();
  const starterRows = (starters.data?.starters ?? []).filter((s) => !s.done);
  // №16 S4 — workflow rows share this inbox (one home, one vocabulary). "Ready
  // for your review" = decisions + agent drafts + ready steps; waits sit apart.
  const wf = useWorkspaceNeedsYou();
  const wfReview = (wf.data?.items ?? []).filter((it) => it.kind !== "wait");
  const wfWaiting = (wf.data?.items ?? []).filter((it) => it.kind === "wait");
  // №22 (D-CL2) — agent escalations raised mid-run (needs_you_reason). Each row
  // deep-links INTO the task's execution thread at the escalation (/app/task/:id).
  // The ["needs-you"] cache is shared with the nav badge and the desk (answering
  // anywhere clears it everywhere). It caches the whole ENVELOPE and each consumer
  // `select`s what it needs — one fetch, not one per reader. The first cut gave the
  // scope its own query key, which re-requested the identical tool call on every
  // load; react-query dedupes by KEY, so a second key is a second request.
  const escQuery = useQuery({
    queryKey: NEEDS_YOU_KEY,
    queryFn: () => mcpClient.listNeedsYou(),
    staleTime: 5_000,
    select: (f) => f.tasks,
  });
  // №22.6 (D-NY1) — the scope verdict for the CARD feed, off the SAME cache entry.
  const escScopeQuery = useQuery({
    queryKey: NEEDS_YOU_KEY,
    queryFn: () => mcpClient.listNeedsYou(),
    staleTime: 5_000,
    select: (f) => f.scope,
  });
  const escScope = escScopeQuery.data;
  const escalations = (escQuery.data ?? []).filter((t) => (t.needs_you_reason ?? "").trim().length > 0);
  const answer = useAnswerNeedsYou();
  // №22.5 (C4) — skipping is an answer too: it resolves the card with a stated
  // consequence and advances a batch, so it belongs wherever the card is answered.
  const skip = useSkipNeedsYou();
  // №22.5 (F10) — answering in your own words runs the hire with those words,
  // which since F6 arrive alongside the question they answer.
  const play = usePlayTaskRun();
  const pendingAnswerFor = answer.isPending ? answer.variables?.taskId : null;
  // №22.2 (D-CV1) — the receipt: answering resolves the card IN PLACE (a one-line
  // receipt) that fades on next visit, rather than vanishing on the spot.
  const ja = useJustAnswered();
  // №22.3 (D-CH4) — "view thread" retargets to the docked panel, never a route.
  const coPanel = useCoPanel();
  const chooseAndRecord = (t: Task, optionIndex: number) => {
    const opt = t.needs_you_options?.[optionIndex];
    // №22.3 (D-CH2) — the answer choreography. FIRST focus the panel on this task so
    // the run is watched as it lands (the page does not move); the card resolves in
    // place below. notifyAnswered focuses + bumps the reveal nonce.
    coPanel.notifyAnswered({ taskId: t.id, agentId: t.agent_id, agentName: t.agent_name, label: anchorLabelOf(t) });
    answer.mutate(
      { taskId: t.id, optionIndex, ...(opt?.label ? { optionLabel: opt.label } : {}) },
      {
        onSuccess: () =>
          ja.record(t.id, {
            optionIndex, label: opt?.label ?? "your choice", at: new Date().toISOString(), outcome: "executed",
            reason: t.needs_you_reason ?? "", options: t.needs_you_options ?? [], evidence: t.needs_you_evidence ?? [],
            agentName: t.agent_name, anchorLabel: anchorLabelOf(t),
            // №22.5 (F1) — the receipt belongs to THIS question, not to the task.
            ...(t.needs_you_batch ? { batchIndex: t.needs_you_batch.index } : {}),
          }),
      },
    );
  };

  /** №22.5 (F10) — answer in your own words, from the Needs-You home. */
  const replyToCard = (t: Task, text: string) => {
    coPanel.focusTask({ taskId: t.id, agentId: t.agent_id, agentName: t.agent_name, label: anchorLabelOf(t) });
    if (t.agent_id) play.mutate({ agentId: t.agent_id, taskId: t.id, input: text, requestedByName: "you" });
  };

  /** №22.5 (C4/F1) — skip leaves the same kind of receipt, and advances a batch. */
  const skipAndRecord = (t: Task) => {
    coPanel.notifyAnswered({ taskId: t.id, agentId: t.agent_id, agentName: t.agent_name, label: anchorLabelOf(t) });
    skip.mutate({ taskId: t.id }, {
      onSuccess: (res) => ja.record(t.id, {
        optionIndex: -1, label: "Skipped", at: new Date().toISOString(), outcome: "skipped",
        reason: t.needs_you_reason ?? "", options: t.needs_you_options ?? [], evidence: t.needs_you_evidence ?? [],
        agentName: t.agent_name, anchorLabel: anchorLabelOf(t),
        ...(t.needs_you_batch ? { batchIndex: t.needs_you_batch.index } : {}),
        ...(res?.consequence ? { consequence: res.consequence } : {}),
      }),
    });
  };
  // Merge live escalations with just-answered receipts whose task already left the
  // query, so the receipt lingers on THIS surface until the next visit (D-CV1).
  const escIds = new Set(escalations.map((t) => t.id));
  const answeredExtra = Object.entries(ja.map).filter(([id]) => !escIds.has(id));
  const hasDecisions = escalations.length > 0 || answeredExtra.length > 0;

  /**
   * №22.6 (D-NY2) — the merged, ordered DECISIONS feed.
   *
   * Both feeds carry the server's `rank` (a four-number key: class · dated · due ·
   * age). Merging is therefore a sort on a value we were GIVEN, not an ordering
   * this component invents — which matters, because the console cannot import the
   * worker and any rule re-implemented here would rot the moment the ruling moved.
   *
   * `rank` is absent only from a row served by a worker older than this deploy;
   * such a row sorts last rather than crashing or claiming a rank it does not have.
   */
  const decisionFeed = useMemo(() => {
    const LAST: [number, number, number, number] = [9, 9, Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER];
    const entries: { key: string; rank: [number, number, number, number]; card?: Task; wf?: NeedsYouItem }[] = [
      ...escalations.map((t) => ({ key: t.id, rank: t.inbox?.rank ?? LAST, card: t })),
      ...wfReview.map((it) => ({
        key: `${it.instanceNo}-${it.kind}-${it.segmentNo}-${it.stepEntryNo}`,
        rank: it.rank ?? LAST,
        wf: it,
      })),
    ];
    return entries.sort((a, b) => {
      for (let i = 0; i < 4; i++) if (a.rank[i] !== b.rank[i]) return a.rank[i]! - b.rank[i]!;
      // A stable tie-break on the key, so equal-ranked rows never re-order between
      // polls — a list that shuffles under the reader is its own kind of dishonesty.
      return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
    });
  }, [escalations, wfReview]);
  /**
   * №53 (53.1) — TODAY'S ANSWERED DECISIONS, from the durable ledger.
   *
   * The drawing's fifth rail row ("Write off 0.6h on Patel · answered") is a card
   * answered BEFORE this page was opened. `useJustAnswered` cannot supply it: that
   * map is page-local by design (D-CV1) and empty on arrival, so a reader who
   * answered four cards and came back saw a rail that had silently halved — the
   * §22.6 O1 complaint, one surface along.
   *
   * So the rail reads `answered_ledger`, which is what 53.1's spec line asks for.
   * **The same query key `AnsweredStrip` uses**, deliberately: react-query dedupes
   * by key, so this is one request serving two readers, and the two can never
   * disagree about what was answered.
   */
  const answeredSince = todayStartISO();
  const answeredLedger = useQuery<AnsweredDecision[]>({
    queryKey: ["answered-decisions", "", "", 0, "", answeredSince],
    staleTime: 5_000,
    queryFn: () => mcpClient.listAnsweredDecisions({ since: answeredSince, limit: 25 }),
  });

  /**
   * №53 (53.1) — THE RAIL. One row per decision, in the feed's ruled order, plus
   * the receipts whose task has already left the query so an answered row dims in
   * place rather than vanishing (D-CV1, drawn as `.item.done`).
   *
   * The row's title is the TASK's title, not the question. The drawing is explicit
   * about this — the rail reads "Henderson lease — counter-offer" while the card
   * asks "Landlord has countered at $412/m²…" — and it is the right call: a rail of
   * full questions is unscannable, and the question is never truncated where it is
   * actually being answered.
   */
  const rail = useMemo(() => {
    const rows: { key: string; title: string; meta: string; answered: boolean; ledger?: AnsweredDecision }[] = [];
    for (const e of decisionFeed) {
      if (e.wf) {
        rows.push({
          key: e.key,
          title: e.wf.title,
          meta: [e.wf.owner?.label, e.wf.whyNow].filter(Boolean).join(" · "),
          answered: false,
        });
        continue;
      }
      const t = e.card!;
      rows.push({
        key: e.key,
        title: t.title || t.needs_you_reason || "Decision",
        // `whyNow` is SERVER-COMPOSED and already opens with the hire and the time
        // ("Matter Runner · 08:51 · due today" — the drawing's own meta line). The
        // first cut concatenated agent + anchor + whyNow and produced "Matter Runner
        // · Henderson v Coastline Property · MAT-0021 · Matter Runner · 08:51 · due
        // today", naming the hire twice in a 270px column. The anchor is not lost —
        // it is on the card, where there is room to read it.
        meta: t.inbox?.whyNow || [t.agent_name, anchorLabelOf(t)].filter(Boolean).join(" · "),
        answered: !!receiptFor(ja.map[t.id], t),
      });
    }
    for (const [id, r] of answeredExtra as [string, AnsweredReceipt][]) {
      rows.push({ key: id, title: r.reason || "Decision", meta: [r.agentName, r.anchorLabel].filter(Boolean).join(" · "), answered: true });
    }
    // Then the ledger's own, minus anything already represented above — a row must
    // never appear twice because two readers both know about it.
    const seen = new Set(rows.map((r) => r.key));
    for (const a of answeredLedger.data ?? []) {
      const key = a.taskId ?? a.id;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({
        key,
        title: a.question,
        meta: [a.agentName, a.anchorName].filter(Boolean).join(" · "),
        answered: true,
        ledger: a,
      });
    }
    return rows;
  }, [decisionFeed, answeredExtra, ja.map, answeredLedger.data]);

  // The open card. Defaults to the first row still needing an answer, and follows
  // the feed rather than pinning an index: a row that resolves under the reader
  // must not silently open a different card, so the selection is held BY KEY.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const firstOpenKey = rail.find((r) => !r.answered)?.key ?? rail[0]?.key ?? null;
  const openKey = rail.some((r) => r.key === selectedKey) ? selectedKey : firstOpenKey;
  const nextOpen = rail.find((r) => !r.answered && r.key !== openKey) ?? null;
  const openEntry = decisionFeed.find((e) => e.key === openKey) ?? null;
  const openTask = openEntry?.card ?? null;
  const openReceipt = openTask ? receiptFor(ja.map[openTask.id], openTask) : null;
  const openExtra = !openEntry && openKey ? (ja.map[openKey] ?? null) : null;
  // A ledger row has no live task and no page-local receipt — it is history, and it
  // renders the same result panel from the same fields.
  const openLedger = !openEntry && !openExtra && openKey
    ? (rail.find((r) => r.key === openKey)?.ledger ?? null)
    : null;

  // №53 (53.1) — TAKE OVER, on every card. The affordance and the verb both already
  // existed (`DecisionCard`'s `onTakeOver`, `take_over_task`); this surface simply
  // never passed the prop, so the escape hatch was reachable from the task page and
  // nowhere a decision is actually taken.
  const takeOver = useTakeOverTask();

  // №22.1 (D-CC8) — WHILE YOU WERE AWAY: the payoff strip (what ran without needing you).
  const away = useWorkspaceActivity(6);
  const awayRows = away.data ?? [];

  const auths = pending.data?.authorisations ?? [];
  // №22.6 (D-NY1) — what the contract withheld, from BOTH feeds. Reported, never
  // silently dropped: the whole point of the scope is that the reader can trust
  // "nothing needs you" without wondering what it swallowed.
  const withheld = (wf.data?.scope?.filtered ?? 0) + (escScope?.filtered ?? 0);
  const decisionCount = escalations.length + wfReview.length;
  /* The drawing's own caption, and its arithmetic: "4 decisions · est. 8 min to
   * clear" is two minutes a decision. The second sentence is the surface's whole
   * proposition and is not ours to paraphrase. */
  /* D-NY1 rides along: what the scope withheld is still STATED. It used to live in
   * the crumb caption that crew's lead replaced, and dropping it with that caption
   * would have re-opened the §22.6 O1 complaint — a home that quietly halves itself
   * is indistinguishable from data loss. It is a clause, not a second line. */
  const withheldClause = withheld ? ` ${withheld} more on their matters.` : "";
  /* The counts stay in the lead, and not only for the reader. `shell-s4-29`'s glance
   * check reads this line as the surface's OWN statement of what needs a human and
   * compares it to the rail badge — an agreement check that would go vacuous the
   * moment the line stopped saying "N approvals". Crew's rhythm is dot-separated
   * clauses then the proposition, so they fit it rather than fight it. */
  const countClauses = [
    decisionCount ? `${decisionCount} decision${decisionCount === 1 ? "" : "s"}` : "",
    auths.length ? `${auths.length} approval${auths.length === 1 ? "" : "s"}` : "",
    wfWaiting.length ? `${wfWaiting.length} wait${wfWaiting.length === 1 ? "" : "s"}` : "",
  ].filter(Boolean);
  const leadLine = decisionCount
    ? `${countClauses.join(" · ")} · est. ${decisionCount * 2} min to clear. Each one has the work already done — you decide, the crew carries.${withheldClause}`
    : countClauses.length
      ? `${countClauses.join(" · ")}. Nothing needs your judgement right now.${withheldClause}`
      : `All clear. Anything new the crew can't decide will appear here.${withheldClause}`;

  return (
    <div className="screen">
      {/* №29 (S2) — the CARDS subject. "Needs you" was the page's `<h1>` directly
          under a stage head that reads "Everywhere", so the surface named itself
          twice. The name goes up ("Everywhere / Needs you"); the count line stays
          with the cards it counts. */}
      {/* №22.6 (D-NY1/D-NY2) — the caption now describes the order it actually
          keeps, and says what it is NOT showing. "oldest first" was the old
          caption over "everything actionable in projection order"; the real rule
          is judgement first, then due-then-age with overdue floating.
          `withheld` is Sprint 19's no-silent-truncation rule applied to the
          inbox: a home that quietly halves itself and says nothing is
          indistinguishable from the data loss O1 was raised about. */}
      {/* №53 — NO crumb title on this surface. §29 removed page titles that repeat
       * their anchor ("two titles, one surface"); crew puts the surface's name on
       * the surface, in serif, at 22px. Both cannot be true at once — declaring it
       * here as well put "Needs you" on screen twice, forty pixels apart, which the
       * 22.6 readiness probe caught as a strict-mode violation before any human saw
       * it. The DRAWING wins: the view owns the name, the crumb keeps the anchor. */}


      {/* ═══ DECISIONS — the two-pane (53.1): the rail, and the open card ═══
          Drawn in `docs/53-mockups/needs-you-1.png` (approved). The stack this
          replaced rendered every card at full height, so a four-decision morning
          was four screens of scrolling and the second card was never visible while
          the first was being read. The rail makes the queue legible; the card pane
          is where the reading and the answering happen. */}
      {(hasDecisions || wfReview.length > 0) && (
        <>
          {/* №53 — crew's view head, and the section head it replaces.
           *
           * The prototype opens the surface with `<h2>Needs you</h2>` and ONE lead
           * line, then goes straight to the two-pane. Ours opened with a caption for
           * a heading that was not there (§29 suppresses a title that repeats its
           * anchor, so the name lived only in the crumb) and then interposed a
           * "DECISIONS · your judgement" section rule between the reader and the
           * queue. The drawing has neither. A section header over the only section
           * on the page is a label for a room you are already standing in. */}
          <div className="viewhead">
            <h2>Needs you</h2>
            <p className="lead" data-testid="table-caption">{leadLine}</p>
          </div>
          <div className="nypanes" data-testid="needs-you-panes">
            <div className="nylist" role="tablist" aria-label="Decisions" data-testid="needs-you-rail">
              {rail.map((r, i) => (
                <button
                  key={r.key}
                  type="button"
                  role="tab"
                  aria-selected={r.key === openKey}
                  className={`nyitem${r.key === openKey ? " nyitem--on" : ""}${r.answered ? " nyitem--done" : ""}`}
                  onClick={() => setSelectedKey(r.key)}
                  data-testid="needs-you-rail-item"
                  data-index={i}
                >
                  <span className="nyitem__t">
                    {r.title}
                    {/* Real text, not a CSS `::after` — a state a screen reader
                        cannot reach is not a state the product has. */}
                    {r.answered ? <span className="nyitem__answered"> · answered</span> : null}
                  </span>
                  {r.meta ? <span className="nyitem__m">{r.meta}</span> : null}
                </button>
              ))}
            </div>

            <div data-testid="needs-you-stage">
              {/* ── ANSWERED → the result panel (drawn in needs-you-2.png). ──
                  The bullets are what the card ITSELF stated would happen — the
                  chosen option's `consequence`, and the durable one-line account
                  when the receipt carries one. The drawing shows four; this shows
                  what exists. D-CR2: a panel that invents its own account of what
                  the system did is the one thing an audit surface may never do. */}
              {(openReceipt ?? openExtra ?? openLedger) ? (() => {
                const r = openLedger
                  ? { label: openLedger.optionLabel, outcome: openLedger.outcome, consequence: openLedger.consequence, optionIndex: -1, options: [] as NeedsYouOption[], line: openLedger.line }
                  : { ...(openReceipt ?? openExtra)!, line: undefined as string | undefined };
                // The two honest sources, in the order the card presented them:
                // what the CHOSEN option said it would do, and what a skip recorded.
                // Neither is composed here — both are snapshots taken at raise time,
                // which is why this panel can still be right a year later.
                const chosen = r.optionIndex >= 0 ? r.options?.[r.optionIndex] : undefined;
                const bullets = [chosen?.consequence, r.consequence, r.line].filter((x): x is string => !!x && x.trim().length > 0);
                const verb = r.outcome === "skipped" ? "Skipped" : r.outcome === "failed" ? "Didn’t complete" : "Done";
                return (
                  <div className="nyresult" data-testid="needs-you-result">
                    <h4 className="nyresult__h">{verb} — {r.label ?? "your choice"}</h4>
                    {bullets.length > 0 ? (
                      <ul>{bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
                    ) : (
                      /* No stated consequence is a fact about the card, said plainly
                         rather than papered over with a reassuring sentence. */
                      <p className="nyresult__next">The card recorded no consequence for this option.</p>
                    )}
                    <div className="nyresult__next">
                      {nextOpen ? "Next: the rest of your queue is on the left." : "Nothing else needs you right now."}
                    </div>
                    <div className="nyresult__acts">
                      {nextOpen && (
                        <button type="button" data-variant="primary" onClick={() => setSelectedKey(nextOpen.key)} data-testid="needs-you-next-card">
                          Next card → {nextOpen.title}
                        </button>
                      )}
                      {openTask && (
                        <button
                          type="button"
                          onClick={() => coPanel.focusTask({ taskId: openTask.id, agentId: openTask.agent_id, agentName: openTask.agent_name, label: anchorLabelOf(openTask) })}
                          data-testid="needs-you-inspect"
                        >
                          Inspect what was sent
                        </button>
                      )}
                    </div>
                  </div>
                );
              })() : openEntry?.wf ? (
                openEntry.wf.rendersAsCard
                  ? <WorkflowDecisionCard it={openEntry.wf} />
                  : <WorkflowGlanceRow it={openEntry.wf} />
              ) : openTask ? (
                <DecisionCard
                  key={openTask.id}
                  variant="full"
                  reason={openTask.needs_you_reason ?? ""}
                  evidence={openTask.needs_you_evidence}
                  options={openTask.needs_you_options}
                  context={{ agentName: openTask.agent_name, anchorLabel: anchorLabelOf(openTask), age: openTask.inbox?.whyNow }}
                  resolved={null}
                  // PIN THE SELECTION BEFORE ANSWERING. Without this the stage jumps
                  // to the next unanswered card the instant the answer lands — the
                  // selection falls back to "first open", and the card you just
                  // decided is no longer the first open one — so the result panel
                  // renders for nobody and mockup 2 never appears. Answering is a
                  // RECEIPT (D-CV1); a receipt you are navigated away from is a
                  // disappearance with extra steps. Found by the gate, not by reading.
                  onChoose={(optionIndex) => { setSelectedKey(openKey); chooseAndRecord(openTask, optionIndex); }}
                  choosing={pendingAnswerFor === openTask.id}
                  chosenIndex={pendingAnswerFor === openTask.id ? (answer.variables?.optionIndex ?? null) : null}
                  batch={openTask.needs_you_batch ?? null}
                  provenance={openTask.provenance ?? null}
                  skipConsequence={openTask.needs_you_skip_consequence ?? null}
                  skipped={openTask.needs_you_skipped}
                  onSkip={() => { setSelectedKey(openKey); skipAndRecord(openTask); }}
                  onReply={(text) => replyToCard(openTask, text)}
                  replying={play.isPending && play.variables?.taskId === openTask.id}
                  skipping={skip.isPending && skip.variables?.taskId === openTask.id}
                  // 53.1 — take over, on EVERY card. The prop and the verb both
                  // already existed; this surface never passed them.
                  onTakeOver={() => takeOver.mutate({ taskId: openTask.id })}
                  takingOver={takeOver.isPending && takeOver.variables?.taskId === openTask.id}
                  showOptionKeys
                  openThreadHref={`/app/task/${openTask.id}`}
                  onOpenThread={() => coPanel.focusTask({ taskId: openTask.id, agentId: openTask.agent_id, agentName: openTask.agent_name, label: anchorLabelOf(openTask) })}
                  error={answer.isError && answer.variables?.taskId === openTask.id ? ((answer.error as Error)?.message ?? "That option couldn’t be completed.") : null}
                  data-testid="needs-you-decision"
                />
              ) : null}
            </div>
          </div>
        </>
      )}

      {/* ═══ APPROVALS — money · the I9 loop, never one-click from here ═══ */}
      <div className="sechead"><h2>Approvals</h2>{!pending.isLoading && !pending.isError ? <span className="n">money — the I9 loop, never one-click from here</span> : null}<div className="rule" /></div>

      {pending.isLoading ? (
        <div className="inbox"><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>
      ) : pending.isError && isDeniedError(pending.error) ? (
        // DENIED — a permission boundary, not a failure. Teaching copy, no outage,
        // no Retry (retrying won't grant access). Distinct from the empty state.
        <div className="surface"><div className="surface__section"><div className="empty-state">
          <svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.3}><path d="M12 3a4 4 0 0 0-4 4v3H6.5A1.5 1.5 0 0 0 5 11.5v7A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 17.5 10H16V7a4 4 0 0 0-4-4Zm-2 7V7a2 2 0 1 1 4 0v3Z" /></svg>
          <h3>This inbox is role-scoped</h3>
          <p>Authorisations appear here only for the ledgers you sign on. You don’t hold a trust role on this workspace — ask a workspace owner to add you as a signatory (or grant a trust role) if you expect to review posts here.</p>
        </div></div></div>
      ) : pending.isError ? (
        // UNREACHABLE — a genuine transport failure ONLY. Outage copy + Retry.
        <div className="surface"><div className="surface__section"><div className="error-state">
          <svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4}><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>
          <h3>Couldn’t load approvals</h3>
          <p>We couldn’t reach the ledger service. Nothing was approved or declined. Your pending items are unchanged — retry when you’re ready.</p>
          <button data-variant="primary" onClick={() => pending.refetch()}>Retry</button>
        </div></div></div>
      ) : auths.length === 0 ? (
        <div className="surface"><div className="surface__section"><div className="empty-state">
          <svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.3}><path d="M20 6 9 17l-5-5" /></svg>
          <h3>Nothing needs your authorisation</h3>
          <p>Pending posts, closes and finalisations for ledgers you sign on will appear here. Non-signatories never see this inbox — it is role-scoped, not greyed out.</p>
        </div></div></div>
      ) : (
        <div className="inbox">
          {auths.map((a) => <AuthRow key={a.id} auth={a} defaultOpen={a.id === deepLinkAuth} />)}
        </div>
      )}

      {/* ═══ WAITS — waiting on the outside world · clear it if it's arrived ═══ */}
      {wfWaiting.length > 0 && (
        <>
          <div className="sechead"><h2>Waiting on the outside world</h2><span className="n">clear it if it's arrived</span><div className="rule" /></div>
          <div className="stack">{wfWaiting.map((it) => <WorkflowGlanceRow key={`${it.instanceNo}-wait-${it.stepEntryNo}`} it={it} />)}</div>
        </>
      )}

      {/* ═══ WHILE YOU WERE AWAY — the payoff: what ran without needing you ═══ */}
      {awayRows.length > 0 && (
        <>
          <div className="sechead"><h2>While you were away</h2><span className="n">ran without needing you — the reason you hired them</span><div className="rule" /></div>
          <div style={{ background: "color-mix(in srgb, var(--ok) 9%, var(--surface-1))", border: "1px solid color-mix(in srgb, var(--ok) 28%, transparent)", borderRadius: 13, padding: "10px 16px" }}>
            {awayRows.map((a) => (
              <div key={a.id} style={{ display: "flex", gap: 9, fontSize: 12.5, color: "var(--text-2)", padding: "3px 0" }}>
                <span style={{ color: "var(--ok)", fontWeight: 700 }} aria-hidden="true">✓</span>
                <span>{a.summary}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ---- Get set up (first-run starter rows, D-SH6) -------------------- */}
      {starterRows.length > 0 && (
        <>
          <div className="sechead"><h2>Get set up</h2><span className="n">{starterRows.length}</span><div className="rule" /></div>
          <div className="stack">
            {starterRows.map((s) => {
              // №22.3 (D-CH3) — an internal deep link routes client-side (no full
              // reload); only a genuinely external target uses a raw anchor.
              const internal = s.deep_link?.startsWith("/");
              const inner = (
                <>
                  <span className="listrow__marker listrow__marker--todo" aria-hidden="true" />
                  <div className="listrow__main">
                    <div className="t">{s.title}</div>
                    {s.body ? <div className="d">{s.body}</div> : null}
                  </div>
                  <span className="listrow__go">Open →</span>
                </>
              );
              return internal ? (
                <Link key={s.id} className="listrow listrow--accent" to={s.deep_link}>{inner}</Link>
              ) : (
                <a key={s.id} className="listrow listrow--accent" href={s.deep_link} target="_blank" rel="noreferrer">{inner}</a>
              );
            })}
          </div>
        </>
      )}

      {/* ═══ ANSWERED TODAY — MOVED TO HANDLED (№53, E-27) ═══════════════════
          The strip is not deleted; it moved. David ruled that the rail wins: with
          `Needs you` and `Handled` both on it, a page about what still needs you
          carrying a second history of what no longer does is the rail's own job
          done twice, in the wrong place.

          It renders on `/app/handled` now, from the SAME `AnsweredStrip`
          component the matter cockpit uses — §22.6's H2/O1 account still exists
          and is still server-backed, and its assertion in `test:needs-you-22.6`
          is RE-POINTED at Handled rather than dropped. D-CV1 is untouched:
          answering is still a receipt, it is just filed with the day's other
          receipts. */}

      {/* ---- Decided (history) --------------------------------------------- */}
      {(decided.data?.authorisations.length ?? 0) > 0 && (
        <>
          <div className="sechead"><h2>Decided</h2><span className="n">recent</span><div className="rule" /></div>
          <div className="surface"><div className="surface__section">
            <table className="ledger-table">
              <thead><tr><th>Subject</th><th>Outcome</th><th>By</th><th>When</th><th>Evidence</th></tr></thead>
              <tbody>
                {decided.data!.authorisations.map((d) => (
                  <tr key={d.id}>
                    <td>{d.title}{d.gloss ? <span className="muted"> — {d.gloss}</span> : null}</td>
                    <td><span className={`pill ${OUTCOME_PILL[d.status] ?? "pill--draft"}`}><span className="dot" />{d.status}</span></td>
                    <td>{d.approved_by ? "signatory" : "—"}</td>
                    <td className="row-date">{new Date(d.created_at + (d.created_at.endsWith("Z") ? "" : "Z")).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</td>
                    <td>{d.status === "declined" && d.declined_reason ? <span className="muted">reason: {d.declined_reason}</span> : <HashChip hash={d.content_hash} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div></div>
        </>
      )}
    </div>
  );
}
